// src/components/payment.jsx
// Handles ALL payment modals via Monnify: bookings, subscriptions (monthly + yearly), style boosts.
// subscriptionTier and subscriptionEndDate are written here — useSubscription.js reads them.

import React, { useState } from 'react';
import {
  doc, updateDoc, serverTimestamp, addDoc,
  collection, Timestamp, getDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useSubscription, PRICING, formatNaira } from '../hooks/useSubscription';
import toast from 'react-hot-toast';

const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function Payment({
  bookingId,
  amount,
  email,
  onSuccess,
  onClose,
  isSubscription = false,
  subscriptionTier = 'yearly', // 'monthly' | 'yearly'
  isBoost = false,
  styleId = null,
}) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // ── Firestore writes after verified payment ─────────────────────────────────
  const handleFinalSuccess = async (reference) => {
    try {
      if (isBoost && styleId) {
        // 1. STYLE BOOST
        await updateDoc(doc(db, 'global_gallery', styleId), {
          isPromoted: false,
          boostPaid: true,
          paymentReference: reference,
          promotionStatus: 'pending_approval',
        });

        await addDoc(collection(db, 'transactions'), {
          type: 'style_boost',
          styleId,
          amount,
          reference,
          createdAt: serverTimestamp(),
          status: 'success',
        });
        toast.success('BOOST ACTIVE: Awaiting Admin Review 🚀');

      } else if (isSubscription) {
        // 2. ESDRAS SHIELD — monthly or yearly
        const userRef = doc(db, 'users', bookingId);
        const now = new Date();
        const expiryDate = new Date(now);

        if (subscriptionTier === 'monthly') {
          expiryDate.setMonth(expiryDate.getMonth() + 1);
        } else {
          expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        }

        await updateDoc(userRef, {
          // Canonical subscription fields
          subscriptionTier: subscriptionTier,
          subscriptionStatus: 'active',
          subscriptionEndDate: Timestamp.fromDate(expiryDate),
          subscriptionStartDate: serverTimestamp(),

          // Reset limits
          monthlyScansUsed: 0,
          monthlyScansResetDate: serverTimestamp(),

          // Legacy fields for backward compatibility
          isPremium: true,
          isSubscribed: true,
          premiumSince: serverTimestamp(),
          subscriptionExpires: Timestamp.fromDate(expiryDate),
          paymentReference: reference,
          scanCount: 0,
          scansUsed: 0,
          tryOnCount: 0,
          role: 'premium',
          updatedAt: serverTimestamp(),
        });

        await addDoc(collection(db, 'transactions'), {
          userId: bookingId,
          amount,
          type: `subscription_${subscriptionTier}`,
          plan: subscriptionTier === 'monthly' ? 'Monthly Shield' : 'Annual Shield',
          reference,
          status: 'success',
          createdAt: serverTimestamp(),
        });

        toast.success(`ESDRAS SHIELD ACTIVATED — ${subscriptionTier.toUpperCase()} PLAN 👑`);

      } else {
        // 3. BOOKING ESCROW
        await updateDoc(doc(db, 'bookings', bookingId), {
          status: 'paid',
          escrowStatus: 'held',
          paidAt: serverTimestamp(),
          paymentReference: reference,
        });
        toast.success('FUNDS SECURED IN ESCROW 🛡️');
      }

      onSuccess?.(reference);
      onClose?.();
    } catch (e) {
      console.error("Database Update Error:", e);
      setError("Payment verified, but account update failed. Ref: " + reference);
    }
  };

  // ── Monnify SDK ─────────────────────────────────────────────────────────────
  const handleMonnify = async () => {
    setProcessing(true);
    setError(null);

    if (!window.MonnifySDK) {
      setProcessing(false);
      return setError('Payment gateway not loaded. Please refresh the page.');
    }

    let incomeSplitConfig = [];

    // 10/90 split only for booking payments (not subscriptions or boosts)
    if (!isSubscription && !isBoost && bookingId) {
      try {
        const bookingSnap = await getDoc(doc(db, 'bookings', bookingId));
        const stylistId = bookingSnap.data()?.stylistId;
        const stylistSnap = await getDoc(doc(db, 'barbers', stylistId));
        const subAcctCode = stylistSnap.data()?.monnifySubAccountCode;

        if (subAcctCode) {
          incomeSplitConfig = [{
            subAccountCode: subAcctCode,
            feePercentage: 0,
            splitPercentage: 90,
            feeBearer: false,
          }];
        }
      } catch (err) {
        console.warn('Could not fetch split config — proceeding with standard payment.');
      }
    }

    const refPrefix = isBoost ? 'BST' : isSubscription ? `SUB_${subscriptionTier.toUpperCase()}` : 'BK';
    const planLabel = isSubscription
      ? `Esdras Shield ${subscriptionTier === 'monthly' ? 'Monthly' : 'Annual'}`
      : isBoost ? 'Style Boost' : 'Booking Payment';

    window.MonnifySDK.initialize({
      amount,
      currency: 'NGN',
      reference: `ESD_${refPrefix}_${Date.now()}`,
      customerFullName: email ? email.split('@')[0] : 'Esdras Customer',
      customerEmail: email || 'customer@esdras.com',
      apiKey: process.env.REACT_APP_MONNIFY_API_KEY,
      contractCode: process.env.REACT_APP_MONNIFY_CONTRACT_CODE,
      paymentDescription: planLabel,
      incomeSplitConfig,
      metadata: {
        subscriptionTier: isSubscription ? subscriptionTier : null,
        type: isSubscription ? 'subscription' : isBoost ? 'boost' : 'booking',
      },
      onComplete: (response) => {
        setProcessing(false);
        if (response.status === 'SUCCESS') {
          handleFinalSuccess(response.transactionReference);
        } else {
          setError('Payment was not completed. Please try again.');
        }
      },
      onClose: () => setProcessing(false),
    });
  };

  // ── UI ──────────────────────────────────────────────────────────────────────
  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={shieldIcon}>
          {isBoost ? '🚀' : isSubscription ? '👑' : '🛡️'}
        </div>

        <h1 style={headerStyle}>
          {isBoost ? 'boost style' : isSubscription ? 'activate shield' : 'secure payment'}
        </h1>

        <p style={subHeaderStyle}>
          {formatNaira(amount)}
          {isSubscription && (
            <span style={{ opacity: 0.6 }}>
              {' '}· {subscriptionTier === 'monthly' ? 'Monthly' : 'Annual'} Plan
            </span>
          )}
          {!isSubscription && ' · Escrow Protection Active'}
        </p>

        {error && <p style={errorStyle}>{error}</p>}

        <div style={infoBox}>
          <p style={infoText}>🔒 Verified Secure Transaction via Monnify Gateway</p>
        </div>

        <button
          onClick={handleMonnify}
          disabled={processing}
          style={{ ...primaryBtn, opacity: processing ? 0.7 : 1 }}
          onMouseOver={(e) => !processing && (e.currentTarget.style.transform = 'scale(1.02)')}
          onMouseOut={(e) => !processing && (e.currentTarget.style.transform = 'scale(1)')}
        >
          {processing ? 'VERIFYING...' : `PAY ${formatNaira(amount)}`}
        </button>

        <button onClick={onClose} disabled={processing} style={cancelBtn}>
          CANCEL & RETURN
        </button>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const overlayStyle = { 
  position: 'fixed', 
  inset: 0, 
  background: 'rgba(0, 0, 0, 0.96)', 
  display: 'grid', 
  placeItems: 'center', 
  zIndex: 9999, 
  padding: '1rem', 
  backdropFilter: 'blur(10px)' 
};

const modalStyle = { 
  background: NAVY, 
  color: 'white', 
  padding: '3rem 2rem', 
  borderRadius: '35px', 
  border: `2px solid ${GOLD}`, 
  width: '100%', 
  maxWidth: '420px', 
  textAlign: 'center', 
  boxShadow: '0 25px 60px rgba(0,0,0,0.8)', 
  fontFamily: 'Montserrat, sans-serif' 
};

const shieldIcon = { fontSize: '3.5rem', marginBottom: '1rem' };

const headerStyle = { 
  fontSize: '1.8rem', 
  color: GOLD, 
  fontWeight: '800', 
  margin: '0 0 0.5rem', 
  textTransform: 'lowercase', 
  letterSpacing: '1px' 
};

const subHeaderStyle = { 
  fontSize: '0.85rem', 
  opacity: 0.7, 
  marginBottom: '2rem', 
  lineHeight: '1.5' 
};

const errorStyle = { 
  color: '#ff6b6b', 
  fontSize: '0.8rem', 
  marginBottom: '1.5rem', 
  background: 'rgba(255,107,107,0.1)', 
  padding: '12px', 
  borderRadius: '10px', 
  border: '1px solid rgba(255,107,107,0.2)' 
};

const infoBox = { 
  background: 'rgba(255,255,255,0.03)', 
  padding: '1rem', 
  borderRadius: '15px', 
  marginBottom: '2rem', 
  border: '1px solid rgba(255,255,255,0.05)' 
};

const infoText = { 
  fontSize: '0.7rem', 
  opacity: 0.5, 
  margin: 0, 
  letterSpacing: '0.5px' 
};

const primaryBtn = { 
  width: '100%', 
  background: GOLD, 
  color: 'black', 
  border: 'none', 
  padding: '1.3rem', 
  borderRadius: '18px', 
  fontSize: '1.1rem', 
  fontWeight: '900', 
  cursor: 'pointer', 
  transition: 'all 0.3s ease', 
  boxShadow: `0 10px 20px rgba(184, 134, 11, 0.2)` 
};

const cancelBtn = { 
  marginTop: '1.5rem', 
  background: 'none', 
  border: 'none', 
  color: '#888', 
  cursor: 'pointer', 
  fontSize: '0.8rem', 
  fontWeight: 'bold', 
  textTransform: 'uppercase', 
  letterSpacing: '1px' 
};
